import React, { useEffect, useState } from 'react';
import { Sparkles, Layers, Shield, Clock, Palette, MessageCircle, MapPin, Calendar, Award, Heart, Star, Instagram, ChevronRight, BoxIcon, Menu, X } from 'lucide-react';
import { useScreenInit } from './useScreenInit.js';
const WHATSAPP_LINK = 'https://wa.me/1234567890'; // Placeholder
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/arxSD3FnJAQ6QHY5R5pSRL/logo.png';
export function App() {
  useScreenInit(); // Initialize screen state if needed
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    // Handle scrolling to hash on mount or hash change (useful for Screens preview)
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          // Add a small delay to ensure rendering is complete before scrolling
          setTimeout(() => {
            element.scrollIntoView({
              behavior: 'smooth'
            });
          }, 100);
        }
      }
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  return <div className="min-h-screen bg-brand-blush overflow-x-hidden" data-id="element-0">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 w-full bg-white/90 backdrop-blur-md z-40 shadow-sm border-b border-brand-pink" data-id="element-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2">
          <div className="flex justify-between items-center h-20" data-id="element-3">
            {/* Logo */}
            <a href="#hero" className="flex items-center gap-3" data-id="element-4">
              <img src={LOGO_URL} alt="ZAFIRA Logo" className="h-12 w-12 object-contain rounded-full" data-id="element-5" />
              <span className="font-serif text-xl font-semibold text-gray-900 tracking-wide" data-id="element-6">
                ZAFIRA
              </span>
            </a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8" data-id="element-7">
              {[{
              name: 'Servicios',
              href: '#servicios'
            }, {
              name: 'Galería',
              href: '#galeria'
            }, {
              name: 'Precios',
              href: '#precios'
            }, {
              name: 'Horarios',
              href: '#horarios'
            }, {
              name: 'Nosotros',
              href: '#nosotros'
            }].map(link => <a key={link.name} href={link.href} className="text-sm font-medium text-gray-600 hover:text-brand-rose transition-colors uppercase tracking-wider" data-id="element-8">
                  {link.name}
                </a>)}
              <a href={WHATSAPP_LINK} className="bg-brand-rose text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-opacity-90 transition-colors shadow-sm" data-id="element-9">
                Reservar
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2 text-gray-600 hover:text-brand-rose transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-10">
              {isMobileMenuOpen ? <X size={24} data-id="element-11" /> : <Menu size={24} data-id="element-12" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && <div className="md:hidden bg-white border-t border-brand-pink absolute w-full shadow-soft" data-id="element-13">
            <div className="px-4 pt-2 pb-6 space-y-2 flex flex-col" data-id="element-14">
              {[{
            name: 'Servicios',
            href: '#servicios'
          }, {
            name: 'Galería',
            href: '#galeria'
          }, {
            name: 'Precios',
            href: '#precios'
          }, {
            name: 'Horarios',
            href: '#horarios'
          }, {
            name: 'Nosotros',
            href: '#nosotros'
          }].map(link => <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-800 hover:bg-brand-blush hover:text-brand-rose rounded-xl transition-colors" data-id="element-15">
                  {link.name}
                </a>)}
              <a href={WHATSAPP_LINK} className="mt-4 flex justify-center items-center gap-2 bg-brand-rose text-white px-5 py-3 rounded-full text-base font-medium shadow-sm" data-id="element-16">
                <MessageCircle size={20} data-id="element-17" />
                Reservar Turno
              </a>
            </div>
          </div>}
      </nav>

      {/* Floating WhatsApp Button */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-floating hover:scale-110 transition-transform duration-300 flex items-center justify-center group" aria-label="Contactar por WhatsApp" data-id="element-18">
        <MessageCircle size={28} data-id="element-19" />
        <span className="absolute right-full mr-4 bg-white text-gray-800 px-3 py-1 rounded-2xl text-sm font-medium shadow-soft opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none" data-id="element-20">
          ¡Reservá tu turno!
        </span>
      </a>

      {/* 1. HERO SECTION */}
      <header id="hero" className="relative w-full bg-white flex flex-col items-center pt-20 pb-12" data-id="element-21">
        <div className="w-full max-w-[1400px] mx-auto mb-8 md:px-6 lg:px-8" data-id="element-22">
          <div className="w-full rounded-none md:rounded-[2rem] overflow-hidden shadow-xl" data-id="element-23">
            <img src="https://cdn.magicpatterns.com/uploads/fLzcGPNrmAHQrPFfzukHP4/banner_2.png" alt="ZAFIRA Estudio de Uñas Banner" className="w-full h-auto object-cover" data-id="element-24" />
          </div>
        </div>

        <div className="text-center max-w-2xl mx-auto px-4 sm:px-6 mt-2" data-id="element-25">
          <h1 className="text-2xl md:text-3xl font-serif text-gray-800 mb-2 tracking-tight" data-id="element-26">
            ZAFIRA
            <span className="block text-sm md:text-base text-brand-rose font-sans font-medium mt-1 tracking-widest uppercase" data-id="element-27">
              Estudio de Uñas
            </span>
          </h1>
          <p className="text-lg text-gray-500 font-serif italic mb-8" data-id="element-28">
            "Uñas que hablan por vos"
          </p>

          <a href={WHATSAPP_LINK} className="inline-flex items-center gap-2 bg-brand-rose text-white px-8 py-4 rounded-3xl font-medium text-lg shadow-soft hover:bg-opacity-90 transition-all duration-300 hover:-translate-y-1" data-id="element-29">
            Reservá tu turno
            <ChevronRight size={20} data-id="element-30" />
          </a>

          <div className="mt-12 flex flex-wrap justify-center gap-4 md:gap-8 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-31">
            <span className="flex items-center gap-1" data-id="element-32">
              <Sparkles size={16} className="text-brand-gold" data-id="element-33" /> Soft Gel
            </span>
            <span className="hidden md:inline text-brand-pink" data-id="element-34">•</span>
            <span className="flex items-center gap-1" data-id="element-35">
              <Layers size={16} className="text-brand-gold" data-id="element-36" /> Acrílicas
            </span>
            <span className="hidden md:inline text-brand-pink" data-id="element-37">•</span>
            <span className="flex items-center gap-1" data-id="element-38">
              <Shield size={16} className="text-brand-gold" data-id="element-39" /> Kapping
            </span>
            <span className="hidden md:inline text-brand-pink" data-id="element-40">•</span>
            <span className="flex items-center gap-1" data-id="element-41">
              <div size={16} className="text-brand-gold" data-id="element-42" /> Polygel
            </span>
            <span className="hidden md:inline text-brand-pink" data-id="element-43">•</span>
            <span className="flex items-center gap-1" data-id="element-44">
              <Clock size={16} className="text-brand-gold" data-id="element-45" /> Semi
            </span>
          </div>
        </div>
      </header>

      {/* 2. SERVICES SECTION */}
      <section id="servicios" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-46">
        <div className="text-center mb-16" data-id="element-47">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4" data-id="element-48">
            Nuestros Servicios
          </h2>
          <div className="w-24 h-1 bg-brand-gold mx-auto rounded-full opacity-50" data-id="element-49"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-50">
          {[{
          icon: Sparkles,
          title: 'Soft Gel',
          desc: 'Extensiones ligeras y naturales con acabado perfecto.'
        }, {
          icon: Layers,
          title: 'Uñas Acrílicas',
          desc: 'Durabilidad y resistencia para lucir uñas largas.'
        }, {
          icon: Shield,
          title: 'Kapping',
          desc: 'Baño protector para fortalecer tu uña natural.'
        }, {
          icon: BoxIcon,
          title: 'Polygel',
          desc: 'Lo mejor del acrílico y el gel en un solo sistema.'
        }, {
          icon: Clock,
          title: 'Semipermanente',
          desc: 'Color impecable y brillante por hasta 21 días.'
        }, {
          icon: Palette,
          title: 'Nail Art',
          desc: 'Diseños personalizados y a mano alzada.'
        }].map((service, idx) => <div key={idx} className="bg-brand-pink bg-opacity-30 p-8 rounded-3xl shadow-sm hover:shadow-soft transition-all duration-300 border border-white border-opacity-50 flex flex-col items-center text-center group" data-id="element-51">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 text-brand-rose group-hover:scale-110 transition-transform duration-300 shadow-sm" data-id="element-52">
                <service.icon size={32} strokeWidth={1.5} data-id="element-53" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-gray-900 mb-3" data-id="element-54">
                {service.title}
              </h3>
              <p className="text-gray-600" data-id="element-55">{service.desc}</p>
            </div>)}
        </div>
      </section>

      {/* 3. GALLERY SECTION */}
      <section id="galeria" className="py-20 bg-white px-4 sm:px-6 lg:px-8" data-id="element-56">
        <div className="max-w-7xl mx-auto" data-id="element-57">
          <div className="text-center mb-16" data-id="element-58">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4" data-id="element-59">
              Nuestros Trabajos
            </h2>
            <div className="w-24 h-1 bg-brand-gold mx-auto rounded-full opacity-50" data-id="element-60"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[300px]" data-id="element-61">
            <div className="lg:col-span-2 lg:row-span-2 rounded-3xl overflow-hidden shadow-soft border-4 border-brand-blush relative group" data-id="element-62">
              <img src="https://cdn.magicpatterns.com/uploads/jJN4VGqKy9SWJyyAajQjpx/Soft_gel_🩷.jpg" alt="Soft Gel Nails" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-63" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-64">
                <span className="text-white font-medium text-lg" data-id="element-65">
                  Soft Gel con Diseño
                </span>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-soft border-4 border-brand-blush relative group" data-id="element-66">
              <video src="https://cdn.magicpatterns.com/uploads/jVoR9LzipzaPdrUU6pinrY/AQPSmIBklPdn8WlaHhwt0IjASIoy4t-_dAhygOZhVxUuuVXfXY2XuTo2P_PvNvmvpFaDU4uJ8IG3RDrGTbSD_xVEWqPJ_C5f.mp4" className="w-full h-full object-cover" autoPlay loop muted playsInline data-id="element-67" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-68">
                <span className="text-white font-medium text-lg" data-id="element-69">
                  Nail Art en Acción
                </span>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-soft border-4 border-brand-blush bg-brand-beige flex items-center justify-center p-8 text-center group" data-id="element-70">
              <div data-id="element-71">
                <Instagram size={48} className="mx-auto text-brand-rose mb-4 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-72" />
                <p className="font-serif text-lg text-gray-800" data-id="element-73">
                  Seguinos en Instagram para ver más diseños exclusivos.
                </p>
                <a href="#" className="inline-block mt-4 text-brand-rose font-medium hover:underline" data-id="element-74">
                  @zafira.studiio
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. PRICING SECTION */}
      <section id="precios" className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-75">
        <div className="text-center mb-16" data-id="element-76">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4" data-id="element-77">
            Precios
          </h2>
          <div className="w-24 h-1 bg-brand-gold mx-auto rounded-full opacity-50" data-id="element-78"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-79">
          <div className="bg-white rounded-3xl p-8 shadow-soft border border-brand-pink" data-id="element-80">
            <h3 className="text-2xl font-serif text-gray-900 mb-6 border-b border-brand-blush pb-4" data-id="element-81">
              Servicios Clásicos
            </h3>
            <ul className="space-y-4" data-id="element-82">
              {[{
              name: 'Esmaltado Semipermanente',
              price: '$XXX'
            }, {
              name: 'Kapping Gel/Acrílico',
              price: '$XXX'
            }, {
              name: 'Retiro de material',
              price: '$XXX'
            }, {
              name: 'Manicura Rusa',
              price: '$XXX'
            }].map((item, i) => <li key={i} className="flex justify-between items-center text-gray-700" data-id="element-83">
                  <span data-id="element-84">{item.name}</span>
                  <span className="font-medium text-brand-rose" data-id="element-85">
                    {item.price}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-brand-pink to-brand-blush rounded-3xl p-8 shadow-soft border border-white relative overflow-hidden" data-id="element-86">
            <div className="absolute top-0 right-0 bg-brand-rose text-white text-xs font-bold px-4 py-1 rounded-bl-2xl uppercase tracking-wider" data-id="element-87">
              Más Elegido
            </div>
            <h3 className="text-2xl font-serif text-gray-900 mb-6 border-b border-white/50 pb-4" data-id="element-88">
              Extensiones
            </h3>
            <ul className="space-y-4" data-id="element-89">
              {[{
              name: 'Soft Gel (Largo S/M)',
              price: '$XXX'
            }, {
              name: 'Soft Gel (Largo L/XL)',
              price: '$XXX'
            }, {
              name: 'Acrílicas Esculpidas',
              price: '$XXX'
            }, {
              name: 'Polygel',
              price: '$XXX'
            }].map((item, i) => <li key={i} className="flex justify-between items-center text-gray-800" data-id="element-90">
                  <span className="font-medium" data-id="element-91">{item.name}</span>
                  <span className="font-bold text-brand-rose" data-id="element-92">
                    {item.price}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="text-center mt-12" data-id="element-93">
          <p className="text-gray-500 text-sm mb-6" data-id="element-94">
            * Los precios pueden variar según el diseño de Nail Art elegido.
          </p>
          <a href={WHATSAPP_LINK} className="inline-flex items-center gap-2 bg-white text-brand-rose border-2 border-brand-rose px-8 py-3 rounded-3xl font-medium shadow-sm hover:bg-brand-rose hover:text-white transition-all duration-300" data-id="element-95">
            Consultá por tu servicio
          </a>
        </div>
      </section>

      {/* 5. HOURS & LOCATION SECTION */}
      <section id="horarios" className="py-20 bg-white px-4 sm:px-6 lg:px-8" data-id="element-96">
        <div className="max-w-7xl mx-auto" data-id="element-97">
          <div className="text-center mb-16" data-id="element-98">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4" data-id="element-99">
              Horarios & Ubicación
            </h2>
            <div className="w-24 h-1 bg-brand-gold mx-auto rounded-full opacity-50" data-id="element-100"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-101">
            {/* Schedule */}
            <div className="bg-brand-beige bg-opacity-40 rounded-3xl p-8 md:p-12 shadow-soft" data-id="element-102">
              <div className="flex items-center gap-3 mb-8 text-brand-rose" data-id="element-103">
                <Calendar size={28} data-id="element-104" />
                <h3 className="text-2xl font-serif text-gray-900" data-id="element-105">
                  Horarios de Atención
                </h3>
              </div>
              <div className="space-y-4" data-id="element-106">
                {[{
                day: 'Lunes',
                hours: '10:00 - 19:00'
              }, {
                day: 'Martes',
                hours: '10:00 - 19:00'
              }, {
                day: 'Miércoles',
                hours: '10:00 - 19:00'
              }, {
                day: 'Jueves',
                hours: '10:00 - 19:00'
              }, {
                day: 'Viernes',
                hours: '10:00 - 20:00'
              }, {
                day: 'Sábado',
                hours: '09:00 - 15:00'
              }, {
                day: 'Domingo',
                hours: 'Cerrado'
              }].map((schedule, i) => <div key={i} className="flex justify-between items-center border-b border-gray-200 pb-2 last:border-0 text-gray-700" data-id="element-107">
                    <span className="font-medium" data-id="element-108">{schedule.day}</span>
                    <span data-id="element-109">{schedule.hours}</span>
                  </div>)}
              </div>
            </div>

            {/* Location & Video */}
            <div className="space-y-6" data-id="element-110">
              <div className="bg-white rounded-3xl p-8 shadow-soft border border-brand-pink flex items-start gap-4" data-id="element-111">
                <div className="bg-brand-pink p-3 rounded-2xl text-brand-rose shrink-0" data-id="element-112">
                  <MapPin size={24} data-id="element-113" />
                </div>
                <div data-id="element-114">
                  <h3 className="text-xl font-serif text-gray-900 mb-2" data-id="element-115">
                    Nuestro Estudio
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-116">
                    Plaza Boutique L.25
                    <br data-id="element-117" />
                    Las Piedras, Uruguay
                  </p>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-soft border-4 border-brand-blush relative aspect-[4/3] md:aspect-video" data-id="element-118">
                <video src="https://cdn.magicpatterns.com/uploads/aZMkuEL3SsBsYGsHCvWW27/Estoy muy feliz de contarles que voy a comenzar a trabajar haciendo uñas en este hermoso espacio.mp4" className="w-full h-full object-cover" autoPlay loop muted playsInline data-id="element-119" />
                <div className="absolute inset-0 bg-black/10 pointer-events-none" data-id="element-120"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. ABOUT / TRUST SECTION */}
      <section id="nosotros" className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-blush" data-id="element-121">
        <div className="max-w-7xl mx-auto" data-id="element-122">
          <div className="text-center mb-16" data-id="element-123">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4" data-id="element-124">
              ¿Por qué elegirnos?
            </h2>
            <div className="w-24 h-1 bg-brand-gold mx-auto rounded-full opacity-50" data-id="element-125"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-126">
            {[{
            icon: Award,
            title: 'Técnicas Certificadas',
            desc: 'Profesionales en constante capacitación para brindarte lo último en tendencias.'
          }, {
            icon: Heart,
            title: 'Atención Personalizada',
            desc: 'Cuidamos la salud de tus uñas y te asesoramos según tus necesidades.'
          }, {
            icon: Star,
            title: 'Calidad Premium',
            desc: 'Utilizamos productos de primera línea para garantizar resultados duraderos.'
          }].map((feature, idx) => <div key={idx} className="bg-white p-8 rounded-3xl shadow-soft text-center flex flex-col items-center" data-id="element-127">
                <div className="w-16 h-16 bg-brand-pink rounded-full flex items-center justify-center mb-6 text-brand-rose" data-id="element-128">
                  <feature.icon size={32} strokeWidth={1.5} data-id="element-129" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-gray-900 mb-3" data-id="element-130">
                  {feature.title}
                </h3>
                <p className="text-gray-600" data-id="element-131">{feature.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. CTA FOOTER SECTION */}
      <footer className="bg-white pt-20 pb-10 px-4 sm:px-6 lg:px-8 border-t border-brand-pink" data-id="element-132">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center" data-id="element-133">
          <img src={LOGO_URL} alt="ZAFIRA Logo" className="h-24 w-24 object-contain rounded-full mb-8 shadow-sm" data-id="element-134" />

          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-8" data-id="element-135">
            ¿Lista para tus uñas soñadas?
          </h2>

          <a href={WHATSAPP_LINK} className="inline-flex items-center gap-3 bg-brand-rose text-white px-10 py-5 rounded-full font-medium text-lg shadow-soft hover:bg-opacity-90 transition-all duration-300 hover:-translate-y-1 mb-12" data-id="element-136">
            <MessageCircle size={24} data-id="element-137" />
            Escribinos por WhatsApp
          </a>

          <div className="flex flex-col items-center gap-6 border-t border-brand-blush pt-10" data-id="element-138">
            <a href="#" className="flex items-center gap-2 text-gray-600 hover:text-brand-rose transition-colors" data-id="element-139">
              <Instagram size={24} data-id="element-140" />
              <span className="font-medium" data-id="element-141">@zafira.studiio</span>
            </a>

            <p className="text-gray-400 text-sm" data-id="element-142">
              © {new Date().getFullYear()} Zafira Estudio de Uñas — Las
              Piedras, Uruguay
            </p>
          </div>
        </div>
      </footer>
    </div>;
}